import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { errorBase } from 'utils/ui'

import i18n from 'i18next'
import { Spin } from 'antd'
import { getTempEnvParam } from 'env'
import ResourceService from 'api/Resource'
import RecordDetail from 'pages/records/components/RecordDetail'
import RecordEdit from 'pages/records/components/RecordEdit'
import RecordHistory from 'pages/records/components/RecordHistory'
import RecordSharing from 'pages/records/components/RecordSharing'
import { featureFlags } from 'config/feature-flags'
import { shouldDecryptItem } from './decryptGuard'

// 에러 코드에 따른 에러 메시지 매핑
const ERROR_MESSAGES = {
  430: 'workspace.error.430',
  default: 'workspace.error.null',
}

// 에러 처리 함수
const handleRecordError = (code, message) => {
  console.log(' --- record error ---', message)

  // 431은 expired 상태로 별도 처리되므로 에러 표시 안함
  if (code !== 431 && code !== 870) {
    errorBase({
      title: i18n.t('common.title.warning'),
      content: i18n.t(ERROR_MESSAGES[code] || ERROR_MESSAGES.default),
    })
  }
}

// 응답 처리 함수
const processResourceResponse = (response) => {
  const { data, code, message, headerAccessLevel } = response

  if (code === 200 && data?.resource_key) {
    return {
      success: true,
      code: 200,
      data,
      headerAccessLevel,
    }
  }

  if (code === 431) {
    handleRecordError(code, message)
    return {
      success: false,
      code: 431,
      expiredData: data,
    }
  }

  if (code === 430) {
    handleRecordError(code, message)
    return {
      success: false,
      code: 430,
      data: null,
    }
  }

  handleRecordError(code, message)
  return {
    success: false,
    code,
    data: null,
  }
}

export const fetchIndividualRecord = async (resourceId, locale, callback, isZDW = false) => {
  // 기본 반환 객체
  const defaultResult = {
    success: false,
    data: null,
    code: 0,
  }

  // resourceId가 없으면 바로 반환
  if (!resourceId) {
    await callback(defaultResult)
    return
  }

  try {
    const response = await ResourceService.get({
      id: resourceId,
      locale,
      isZDW,
    })

    const result = processResourceResponse(response)

    console.log('fetchIndividualRecord result:', result)
    await callback(result)
  } catch (error) {
    console.log(error)
    await callback({
      success: false,
      code: -1,
      data: null,
    })
  }
}

export const getRsKeyObjFromResource = async (data) => {
  let rsaEncDec = getTempEnvParam('rsaObj')

  if (data != null && data.group_key != null) {
    const { user_group_key: encGroupKey, group_private_key: encPrivateKey } = data.group_key
    const groupInfo = {
      id: data.id,
      enc_group_key: encGroupKey,
      enc_private_key: encPrivateKey,
      name: data.title,
    }
    rsaEncDec = await rsaEncDec.getGroupObj(groupInfo)
  }

  let rsKeyObj = null

  if (data != null && data.resource_key != null) {
    rsKeyObj = rsaEncDec.createRSKeyObj(data.resource_key.key)
  } else {
    rsKeyObj = rsaEncDec.createRSKeyObj()
  }

  return rsKeyObj
}

export const getData = async (individual) => {
  const data = {
    ...individual,
  }

  const rsKeyObj = await getRsKeyObjFromResource(data)

  try {
    if (rsKeyObj != null && data != null && data.items != null) {
      await Promise.all(
        data.items.map(async (item) => {
          if (shouldDecryptItem(item)) {
            item.content = await rsKeyObj.decrypt(item.content)
          }
          return item
        }),
      )
    }
  } catch (error) {
    console.error(error)
  }

  return data
}

const RecordWrapper = (props) => {
  const {
    type,
    id,
    updatedAt,
    orgChartData,
    pageInfo,
    record,
    // handleViewAllDetails
  } = props
  const [loading, setLoading] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [individual, setIndividual] = useState(null)
  const locale = useSelector((state) => state.settings.locale)

  // const totalCount = (record?.shared_groups?.length ?? 0) + (record?.shared_users?.length ?? 0)
  const tablePage = getTempEnvParam('curTablePage')

  const loadData = (resId, callbackFunc) => {
    setLoading(true)

    fetchIndividualRecord(resId, locale, async (retObj) => {
      const { data, code, expiredData, headerAccessLevel } = retObj
      if (data != null) {
        const decryptedData = await getData(data)
        setIndividual({ ...decryptedData, headerAccessLevel })
      } else if (code === 431) {
        setIndividual({ expired: true, expiredData })
      } else {
        // 메모 모달처럼 목록 페이지 없이 호스팅되는 경우도 있어 옵셔널 호출 (없으면 닫을 대상도 없음)
        tablePage?.openItem(null)
      }
      if (callbackFunc != null) {
        callbackFunc()
      }
      setLoading(false)
    })
  }

  const reloadRecord = (callbackFunc) => {
    loadData(id, callbackFunc)
  }

  useEffect(() => {
    // history 뷰는 individual 을 쓰지 않으므로 재조회를 건너뛴다 — detail→history 전환 시
    // 불필요한 리소스 fetch 로 바깥 Spin 과 RecordHistory 자체 Spin(activities 조회)이
    // 동시에 돌아 로딩 UI 가 두 개 보인다. flag OFF 폴백은 RecordDetail 이라 조회 필요.
    if (type === 'history' && featureFlags.recordChangeHistory) {
      return
    }
    if (id != null) {
      loadData(id)
    } else {
      setIndividual(null)
    }

    // eslint-disable-next-line
  }, [id, updatedAt, type])

  const attrs = {
    ...props,
    orgChartData,
    individual,
    reloadRecord,
    setProcessing,
    getRsKeyObjFromResource,
    loading,
    setLoading,
    pageInfo,
    isLeaked: record?.isLeaked,
    // handleViewAllDetails,
  }

  let Comp = null
  switch (type) {
    case 'detail':
      Comp = RecordDetail
      break
    case 'create':
    case 'edit':
      Comp = RecordEdit
      break
    case 'share':
      Comp = RecordSharing
      break
    case 'history':
      // flag OFF 빌드에서 진입해도 상세로 폴백
      Comp = featureFlags.recordChangeHistory ? RecordHistory : RecordDetail
      break
    default:
      Comp = null
  }

  const spinning = loading === true || processing === true

  // `tip` 은 **blur 대상의 형제**로 렌더돼 흐려지지 않는다(antd 5.21.6 spin/index.js:88-110 —
  // `-blur` 는 `-container` 에만 붙고 tip 은 spinElement 안에 있다). 그래서 폼 잠금
  // (`.ant-spin-blur` 의 `pointerEvents: 'none'`, spin/style/index.js:139-148)을 유지한 채
  // "지금 무슨 일이 일어나는지"만 선명하게 전달할 수 있다.
  // `style` 은 antd 가 `.ant-spin` 요소(dots+tip 오버레이)에 직접 적용한다(spin/index.js:88-91).
  // 이 오버레이는 z-index 4 로 blur 컨테이너(z auto) **위**에 깔리는 형제라, pointer-events 를
  // 끄지 않으면 업로드 중 첨부 행의 취소 X 를 아무리 승격해도 클릭이 여기서 흡수된다
  // (브라우저 probe #1 실측 2026-08-13 — upload_cancel_clickthrough.md R1). **지우면 업로드
  // 취소가 다시 안 눌린다.** 폼 잠금은 이 오버레이가 아니라 베일(.ant-spin-container::after,
  // pointerEvents:auto)이 담당하므로 일반 저장의 잠금은 그대로다.
  return (
    <Spin spinning={spinning} style={{ pointerEvents: 'none' }}>
      {Comp && <Comp {...attrs} />}
    </Spin>
  )
}

export default RecordWrapper
